import { useState, useCallback } from 'react';
import { Result } from '../services/serpApi';
import { SearchControls } from '../pages/Dashboard/components/SearchSection/SearchSection';

interface SearchContext {
  query: string;
  countryCode: string;
  controls: SearchControls;
}

interface SearchState {
  context: SearchContext | null;
  // results armazena o CHUNK completo (máximo de 100 resultados)
  results: Result[]; 
  totalResults: number;
  
  // chunkPage: Qual chunk estamos visualizando (1, 2, 3...) - usado para a API
  chunkPage: number; 
  
  // displayPage: Qual página de exibição estamos (1, 2, 3...) - usado no frontend
  displayPage: number; 
  
  // hasMoreChunks: Indica se há mais chunks para buscar
  hasMoreChunks: boolean;
}

// Variável global (fora do hook) para persistir o estado
let persistedSearchState: SearchState = {
  context: null,
  results: [],
  totalResults: 0,
  chunkPage: 1,
  displayPage: 1,
  hasMoreChunks: false,
};

/**
 * Hook para gerenciar e persistir o estado da busca do Dashboard.
 */
export const useSearchPersistence = () => {
  // Usamos useState apenas para forçar a re-renderização quando o estado persistido muda
  const [state, setState] = useState(persistedSearchState);
  
  const controls = state.context?.controls;
  const chunkResults = state.results;
  
  // Garantir que resultsPerPage seja pelo menos 1
  const resultsPerPage = controls?.resultsPerPage || 20;
  const safeResultsPerPage = Math.max(1, resultsPerPage);
  
  // --- Lógica de Filtragem e Ordenação ---
  
  let filteredAndSortedResults = chunkResults;

  if (controls) {
    // 1. Filtragem
    filteredAndSortedResults = chunkResults.filter(result => {
      // Filtro de Avaliação (Rating)
      if (controls.rating > 0 && (result.rating || 0) < controls.rating) {
        return false;
      }
      
      // Filtro de Status WhatsApp
      if (controls.whatsappStatus === 'verified' && result.status !== 'Validado') {
        return false;
      }
      // Novo filtro: Não Verificado (Pendente ou Inválido)
      if (controls.whatsappStatus === 'unverified_pending' && result.status === 'Validado') {
        return false;
      }
      // Se 'all', passa
      
      return true;
    });
    
    // 2. Ordenação
    filteredAndSortedResults.sort((a, b) => {
      switch (controls.sortBy) {
        case 'name_asc':
          return a.empresa.localeCompare(b.empresa);
        case 'name_desc':
          return b.empresa.localeCompare(a.empresa);
        case 'rating_desc':
          return (b.rating || 0) - (a.rating || 0);
        case 'rating_asc':
          return (a.rating || 0) - (b.rating || 0);
        case 'relevance':
        default:
          // Mantém a ordem original da API (relevância)
          return 0; 
      }
    });
  }
  
  // --- Lógica de Paginação de Exibição ---
  
  // Calcula o total de páginas de exibição dentro do chunk filtrado
  const totalDisplayPages = Math.ceil(filteredAndSortedResults.length / safeResultsPerPage);
  
  // Garante que a página atual não exceda o novo total de páginas após filtragem/mudança de resultsPerPage
  const safeDisplayPage = Math.min(state.displayPage, Math.max(1, totalDisplayPages));
  
  const startIndex = (safeDisplayPage - 1) * safeResultsPerPage;
  const endIndex = startIndex + safeResultsPerPage;
  const displayedResults = filteredAndSortedResults.slice(startIndex, endIndex);
  
  // --- Funções de Atualização ---

  const updateState = useCallback((newState: Partial<SearchState>) => {
    
    const newContext = newState.context;
    const newResultsPerPage = newContext?.controls.resultsPerPage;
    const currentResultsPerPage = persistedSearchState.context?.controls.resultsPerPage;

    // Determine if we should reset the display page (if a new search context is provided, 
    // or if the resultsPerPage setting changed within the new context).
    const shouldResetDisplayPage = 
        !!newContext || 
        (newResultsPerPage !== undefined && newResultsPerPage !== currentResultsPerPage);
        
    persistedSearchState = { 
        ...persistedSearchState, 
        ...newState,
        // Override displayPage if reset is needed
        displayPage: shouldResetDisplayPage ? 1 : (newState.displayPage ?? persistedSearchState.displayPage)
    };
    
    setState(persistedSearchState);
  }, []);

  const clearState = useCallback(() => {
    persistedSearchState = {
      context: null,
      results: [],
      totalResults: 0,
      chunkPage: 1,
      displayPage: 1,
      hasMoreChunks: false,
    };
    setState(persistedSearchState);
  }, []);

  return {
    searchContext: state.context,
    
    // Expondo o chunk completo (bruto)
    currentChunkResults: chunkResults, 
    
    // Expondo o chunk filtrado (para o SelectAllDropdown)
    filteredChunkResults: filteredAndSortedResults,
    
    // Expondo apenas a página atual para exibição
    searchResults: displayedResults, 
    
    totalResults: state.totalResults,
    
    // Paginação do Frontend
    displayPage: safeDisplayPage, // Usando a página segura
    totalDisplayPages: totalDisplayPages,
    resultsPerPage: safeResultsPerPage,
    
    // Paginação do Chunk (para a API)
    chunkPage: state.chunkPage,
    hasMoreChunks: state.hasMoreChunks,
    
    updateSearchState: updateState,
    clearSearchState: clearState,
  };
};